import { EcommStore } from '../internal/stores/EcommStore';
import {
    events,
    on,
    serviceCommands,
    commandProvider,
    queryProvider,
    serviceQueries,
    query,
    emit
} from 'elc-service-bus';
import {
    FORGOT_PASSWORD_ROUTE,
    CART_ROUTE,
    GUEST_ORDER_ROUTE,
    ACCOUNT_ROUTE,
    NOT_FOUND_ROUTE,
    PAYMENT_ROUTE,
    ORDER_HISTORY_ROUTE,
    ACCOUNT_LANDING_ROUTE,
    CONFIRM_REGISTRATION_PATH,
    DRUPAL_ACCOUNT_ROUTE,
    RESET_PASSWORD_PATH,
    SHIPPING_ROUTE,
    CART_ROUTE_PATTERN,
    CHECKOUT_ROUTE_PATTERN,
    ORDER_ROUTE,
    ORDER_ASSOCIATE_ROUTE,
    APPT_BOOKING_ROUTE,
    STORES_ROUTE
} from '../routes';
import { BrowserStorage } from 'elc-universal-utils';

export interface IRequestLoginAndAssociateOrderEventData {
    orderId: string;
    guestToken: string;
}

export const AUTHENTICATION_SOURCES = Object.freeze({
    SIGN_IN: 'sign-in',
    SIGN_UP: 'sign-up',
    FORGOT_PASSWORD: 'forgot-password',
    REFRESH_AUTH_TOKEN: 'refresh-auth-token'
});

export interface ISaveForLaterItem {
    skuId: string;
    quantity: number;
}

export interface IQueryParams {
    [key: string]: string;
}

export interface IRoutingSPPLoaded {
    [key: string]: IQueryParams;
}

export interface IShadeSizePayload {
    productUrl: string;
    productSelection: {
        [key: string]: string;
    };
}

export interface IExternalPaymentErrorRestoreProcessedEventData {
    success: boolean;
}

export class ServiceBusController {
    private ecommStore: EcommStore;

    constructor(ecommStore: EcommStore) {
        this.ecommStore = ecommStore;
    }

    @queryProvider(serviceQueries.GET_CURRENT_ROUTE)
    public getCurrentRoute = () => {
        return {
            currentRoute: this.ecommStore.routerStore.currentRoute,
            params: this.ecommStore.routerStore.params,
            queryParams: this.ecommStore.routerStore.queryParams
        };
    };

    @on(events.CHANGE_LOCATION)
    public changeLocation = async (payload: { redirectUrl: string }) => {
        this.ecommStore.routerStore.changeLocation(payload.redirectUrl);
    };

    @commandProvider(serviceCommands.CHANGE_ROUTE_PROFILE)
    public changeRouteProfile = () => {
        this.ecommStore.routerStore.changeCurrentRoute(ACCOUNT_LANDING_ROUTE);
    };

    @commandProvider(serviceCommands.CHANGE_ROUTE_SETTINGS)
    public changeRouteSettings = () => {
        this.ecommStore.routerStore.changeCurrentRoute(ACCOUNT_ROUTE);
    };

    @commandProvider(serviceCommands.CHANGE_ROUTE_ORDER_HISTORY)
    public changeRouteOrderHistory = () => {
        this.ecommStore.routerStore.changeCurrentRoute(ORDER_HISTORY_ROUTE);
    };

    @on(events.REQUEST_LOGIN_AND_ASSOCIATE_ORDER)
    public onRequestLoginAndAssociateOrder = (ev: IRequestLoginAndAssociateOrderEventData) => {
        const { orderId, guestToken } = ev;
        const url = this.ecommStore.routerStore.getUrlForRoute(ORDER_ASSOCIATE_ROUTE, {
            orderId,
            guestToken
        });
        emit(events.SIGN_IN_LINK_CLICKED, {
            urlRedirectOnSuccess: url
        });
    };

    @on(events.ORDER_PAYMENT_CONFIRMED)
    public changeRouteOrderConfirmation = (payload: { orderId: string }) => {
        this.ecommStore.routerStore.changeCurrentRoute(ORDER_ROUTE, { orderId: payload.orderId });
    };

    @on(events.GUEST_ORDER_PAYMENT_CONFIRMED)
    public changeRouteGuestOrderConfirmation = (payload: {
        guestToken: string;
        orderId: string;
    }) => {
        this.ecommStore.routerStore.changeCurrentRoute(GUEST_ORDER_ROUTE, {
            orderId: payload.orderId,
            guestToken: payload.guestToken
        });
    };

    @on(events.FORGOT_PASSWORD_LINK_CLICK)
    public forgotPasswordLinkClick = () => {
        this.ecommStore.routerStore.changeCurrentRoute(FORGOT_PASSWORD_ROUTE);
    };
    @on(events.FORGOT_PASSWORD_SIGNIN_CLICK)
    public forgotPasswordSignInClick = () => {
        this.ecommStore.routerStore.goBack();
    };
    @on(events.ROUTE_NOT_FOUND)
    public routeNotFound = () => {
        this.ecommStore.routerStore.changeCurrentRoute(NOT_FOUND_ROUTE);
    };
    @on(events.ORDER_HISTORY_REDIRECT)
    public orderHistoryLinkClick = () => {
        this.ecommStore.routerStore.changeCurrentRoute(ORDER_HISTORY_ROUTE);
    };

    @on(events.NAVIGATE_EXTERNAL_URL)
    public paymentUrlChange = (url: string) => {
        this.ecommStore.goToExternalUrl(url);
    };

    @on(events.BACK_HEADER_CLICKED)
    public backHeaderClicked = () => {
        this.ecommStore.routerStore.goBack();
    };

    @on(events.ORDER_HISTORY_NO_ORDERS_LINK_CLICK)
    public orderHistoryNoOrdersLinkClick = async () => {
        const { noOrdersRedirectUrl } = await query(serviceQueries.GET_CONFIG, [
            'noOrdersRedirectUrl'
        ]);
        this.ecommStore.routerStore.changeLocation(noOrdersRedirectUrl);
    };

    @on(events.AUTH_SUCCEDED)
    public authSucceded = async (payload: { source?: string }) => {
        if (payload && payload.source) {
            this.ecommStore.redirectOnSuccess();
        }
    };

    @on(events.CONTINUE_AS_GUEST_CLICKED)
    public guestContinue = () => {
        this.ecommStore.redirectOnSuccess();
    };

    @on(events.NOT_ENOUGH_PRODUCTS)
    public notEnoughProducts = () => {
        this.ecommStore.routerStore.changeCurrentRoute(CART_ROUTE);
    };

    @on(events.SHIPPING_SCREEN_SUBMITTING)
    public shippingScreenSubmitting = () => {
        this.ecommStore.shippingScreenSubmit();
    };

    @on(events.SHOPPING_BAG_BUTTON_CLICKED)
    public shoppingBagButtonClicked = () => {
        this.ecommStore.goToCart();
    };

    @on(events.REVIEW_SCREEN_SUBMITTING)
    public reviewScreenSubmitting = () => {
        this.ecommStore.reviewScreenSubmit();
    };

    @on(events.SHIPPING_SCREEN_FAILURE)
    public shippingScreenFailure = () => {
        this.ecommStore.routerStore.changeCurrentRoute(SHIPPING_ROUTE);
    };

    @on(events.EXTERNAL_PAYMENT_ERROR_RESTORE_PROCESSED)
    public externalPaymentErrorRestoreProcessed = (
        ev: IExternalPaymentErrorRestoreProcessedEventData
    ) => {
        if (ev.success) {
            this.ecommStore.routerStore.changeCurrentRoute(PAYMENT_ROUTE);
        } else {
            this.ecommStore.routerStore.changeCurrentRoute(SHIPPING_ROUTE);
        }
    };

    @on(events.ACCOUNT_SETTINGS_LINK_CLICK)
    public accountSettingsLinkClick = () => {
        this.ecommStore.routerStore.changeCurrentRoute(ACCOUNT_ROUTE);
    };

    @on(events.FORGOT_PASSWORD_SIGNIN_CLICK)
    public forgotPasswordSigninClick = () => {
        this.ecommStore.goToSignIn();
    };
    @on(events.PASSWORD_RESET)
    public passwordReset = () => {
        const savedLocation = BrowserStorage.getItem('resetLocation');

        if (savedLocation === CART_ROUTE_PATTERN || savedLocation === CHECKOUT_ROUTE_PATTERN) {
            this.ecommStore.goToCart();
        } else {
            this.ecommStore.goToAccountSettings();
        }
        BrowserStorage.removeItem('resetLocation');
    };
    @on(events.SIGN_IN_LINK_CLICKED)
    public signInClicked = (payload?: { urlRedirectOnSuccess?: string }) => {
        this.ecommStore.goToSignIn(payload);
    };
    @on(events.SIGN_UP_LINK_CLICKED)
    public signUpClicked = (payload?: { urlRedirectOnSuccess?: string }) => {
        this.ecommStore.goToSignUp(payload);
    };
    @on(events.SIGN_OUT_COMPLETED)
    public signOutCompleted = () => {
        this.ecommStore.goToSignIn();
    };
    @on(events.MOBILE_CART_BADGE_CLICKED)
    public goToCheckout = () => {
        this.ecommStore.goToCart();
    };

    @on(events.REGISTRATION_CONFIRMED)
    public goToAccountSettings = () => {
        this.ecommStore.goToAccountLanding();
    };

    @queryProvider(serviceQueries.CONFIRM_REGISTRATION_TOKEN)
    public getConfirmRegistrationToken = () => {
        return this.ecommStore.routerStore.params.confirmRegistrationToken;
    };

    @on(events.EDIT_ADDRESS_CLICKED)
    public goToShipping = () => {
        this.ecommStore.goToShipping();
    };
    @on(events.ORDER_DETAILS_CLICKED)
    public goToOrderDetails = (id: string) => {
        this.ecommStore.goToOrderDetails(id);
    };

    @queryProvider(serviceQueries.CONFIRM_REGISTRATION_URL)
    public getConfirmRegistrationUrl = () => {
        return `${location.origin}${DRUPAL_ACCOUNT_ROUTE}${CONFIRM_REGISTRATION_PATH}`;
    };

    @queryProvider(serviceQueries.RESET_PASSWORD_URL)
    public getResetPasswordUrl = () => {
        return `${location.origin}${DRUPAL_ACCOUNT_ROUTE}${RESET_PASSWORD_PATH}`;
    };

    @on(events.ORDER_ASSOCIATE_SUCCESS)
    public onOrderAssociateSuccess = () => {
        this.ecommStore.goToAccountLanding();
    };

    @on(events.ORDER_ASSOCIATE_FAILURE)
    public onOrderAssociateFailure = () => {
        this.ecommStore.goToAccountLanding();
    };

    @on(events.NAVIGATE_APPT_BOOKING_REQUESTED)
    public navigateToApptBooking = (queryParams: IQueryParams) => {
        this.ecommStore.routerStore.changeCurrentRoute(APPT_BOOKING_ROUTE, {}, queryParams);
    };

    @on(events.NAVIGATE_STORES_REQUESTED)
    public navigateToStores = () => {
        this.ecommStore.routerStore.changeCurrentRoute(STORES_ROUTE);
    };
}
